import { SortOrder } from 'src/app/core/interface/sort-order';
import { CommonDataService } from 'src/app/core/services/common-data.service';
import { CarSequenceMaintenanceGetRequest } from './interfaces/car-sequence-maintenance-get-request';
import { CarSequenceMaintenanceSaveRequest } from './interfaces/car-sequence-maintenance-save-request';
import { Table } from 'primeng/table';
import { CarSequenceMaintenanceTableColumn } from './interfaces/car-sequence-maintenance-table-column';
import { Option } from './interfaces/option';
import { Component, Injector, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { SessionBaseComponent } from 'src/app/shared/components/session.base.component';
import { CarSequenceMaintenanceService } from '../../service/car-sequence-maintenance.service';
import { CarSequenceMaintenanceTableData } from './interfaces/car-sequence-maintenance-table-data';
import { PageInfo } from 'src/app/core/models/page-info';
import { Calendar } from 'primeng/calendar';
import { isNullOrEmpty } from 'src/app/core/utils/common-functions';
import { throwError } from 'rxjs';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';

@Component({
  selector: 'laas-car-sequence-maintenance',
  templateUrl: './car-sequence-maintenance.component.html',
  styleUrls: ['./car-sequence-maintenance.component.scss']
})

export class CarSequenceMaintenanceComponent
  extends SessionBaseComponent
  implements OnInit {
  readonly viewPermissionCode = PermissionConstants.CarSequenceMaintenance;
  currentLang: string = this.laasSetting.language;
  // Status for the Query Form is open or not
  isSearchFormOpen: boolean = true;
  // Status for the loading circle on the table
  isLoading: boolean = false;
  // Status for enabling a user to edit the data on the table
  isInEditMode: boolean = false;

  // The FormGroup class for the Query Form part
  searchFormGroup: FormGroup;

  // Options for Delivery Category dropdown list
  deliveryCategoryOptions: string[] = [];
  // Options for Pickup Location Type dropdown list
  pickupLocationTypeOptions: string[] = [];
  // Options for ETD dropdown list
  etdOptions: string[] = [];
  // Options for Week Day dropdown list
  weekDayOptions: Option[] = [
    { label: "1 - Monday", value: "1" },
    { label: "2 - Tuesday", value: "2" },
    { label: "3 - Wednesday", value: "3" },
    { label: "4 - Thursday", value: "4" },
    { label: "5 - Friday", value: "5" },
    { label: "6 - Saturday", value: "6" },
    { label: "7 - Sunday", value: "7" }
  ];
  // Options for Assignment District ID dropdown list
  assignmentDistrictIdOptions: Option[] = [];
  // Options for Departure Day dropdown list
  departureDayOptions: string[] = [];
  // Options for Is Work Day dropdown list
  isWorkDayOptions: string[] = [
    "Y", "N"
  ];

  // The table underlying the primeNG table
  @ViewChild("table") table!: Table;
  // All the columns of the table have
  tableColumns: CarSequenceMaintenanceTableColumn[] = [
    { field: 'seqNo', header: 'CarSequenceMaintenance.Table.ColumnHeader.SeqNo', sort: true },
    { field: 'facility', header: 'CarSequenceMaintenance.Table.ColumnHeader.Facility', sort: true },
    { field: 'deliveryCategory', header: 'CarSequenceMaintenance.Table.ColumnHeader.DeliveryCategory', sort: true },
    { field: 'startTime', header: 'CarSequenceMaintenance.Table.ColumnHeader.StartTime', sort: true },
    { field: 'endTime', header: 'CarSequenceMaintenance.Table.ColumnHeader.EndTime', sort: true },
    { field: 'departure', header: 'CarSequenceMaintenance.Table.ColumnHeader.Departure', sort: true },
    { field: 'etd', header: 'CarSequenceMaintenance.Table.ColumnHeader.ETD', sort: true },
    { field: 'assignmentDistrictId', header: 'CarSequenceMaintenance.Table.ColumnHeader.AssignmentDistrictId', sort: true },
    { field: 'pickupLocationType', header: 'CarSequenceMaintenance.Table.ColumnHeader.PickupLocationType', sort: true },
    { field: 'weekDay', header: 'CarSequenceMaintenance.Table.ColumnHeader.WeekDay', sort: true },
    { field: 'isWorkDay', header: 'CarSequenceMaintenance.Table.ColumnHeader.IsWorkDay', sort: true },
  ];
  // The columns user choose to display
  selectedTableColumns: CarSequenceMaintenanceTableColumn[] = [];
  // Current data on the table
  tableData: CarSequenceMaintenanceTableData[] = [];
  // Backup data when data retrieved from the back-end
  backupTableData: CarSequenceMaintenanceTableData[] = [];
  // User current selected row on the table
  selectedRow: CarSequenceMaintenanceTableData | null;
  // User last selected row on the table
  previousSelectedRow: CarSequenceMaintenanceTableData | null;

  // Records for user's operation on the table
  newCarSequenceMaintenanceList: CarSequenceMaintenanceTableData[] = [];
  updatedCarSequenceMaintenanceList: CarSequenceMaintenanceTableData[] = [];
  deletedCarSequenceMaintenanceSeqNoList: number[] = [];

  // For data key to distinguish the added rows on the table
  newDataKeyId: number = 0;
  // Table paging info
  pageInfo: PageInfo = {
    pageSize: 5,
    pageIndex: 0,
    totalRecords: 0,
    totalPages: 0,
  };
  // Table sorting info
  sortOrder: SortOrder = {
    property: "SeqNo",
    direction: -1
  };

  constructor(
    private formBuilder: FormBuilder,
    private injector: Injector,
    private carSequanceMaintenanceService: CarSequenceMaintenanceService,
    private commonDataService: CommonDataService,
  ) {
    super(injector);
    this.searchFormGroup = this.formBuilder.group({
      deliveryCategory: null,
      startTime: null,
      endTime: null,
      assignmentDistrictId: null,
      pickupLocationType: null,
      weekDay: null,
      isWorkDay: null,
      etd: null,
      departureDay: null,
      departureTime: null
    });

    this.selectedTableColumns = this.tableColumns;
    this.selectedRow = null;
    this.previousSelectedRow = this.selectedRow;
  }

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();


    this.deliveryCategoryOptions = [
      "ASSIGNCAR",
      "COLLECTION",
      "EXPORT",
      "HVS",
      "LOCAL",
      "RMB"
    ]

    this.pickupLocationTypeOptions = [
      "M",
      "T"
    ]

    this.etdOptions = [
      "<=[TODAY]",
      "<=[TODAY+1]",
      ">[TODAY]",
      ">[TODAY+1]"
    ]

    this.departureDayOptions = [
      "[ETD]",
      "[FRIDAY]",
      "[TODAY]",
      "[TODAY+1]"
    ]

    // Get AssignmentDistrict Options
    this.commonDataService.getAssignmentDistrictBy(this.laasSetting.facility).subscribe({
      next: (result) => {
        for (const assignmentDistrict of result) {
          this.assignmentDistrictIdOptions.push({
            label: `${assignmentDistrict.assignmentDistrictId} - ${assignmentDistrict.districtName}`,
            value: assignmentDistrict.assignmentDistrictId
          })
        }
      }
    })
  }

  @Input() get selectedColumns(): CarSequenceMaintenanceTableColumn[] {
    return this.selectedTableColumns;
  }

  set selectedColumns(cols: CarSequenceMaintenanceTableColumn[]) {
    this.selectedTableColumns = this.tableColumns.filter(col => cols.includes(col));
  }

  // Set the current time to calendar slot when user click on it
  focusTimePicker(event: any, calendar: Calendar): void {
    const eventTarget = event.target as HTMLInputElement;
    const value = eventTarget.value;
    if (isNullOrEmpty(value)) {
      calendar.updateModel(new Date());
      calendar.updateUI();
      calendar.updateInputfield();
    }
    calendar.filled = value != null && value.length != 0;
  }

  //handle for user input time in 0000 format
  checkTimeInput(event: InputEvent, calendar: Calendar): void {
    const eventTarget = event.target as HTMLInputElement;
    const value = eventTarget.value;

    // Pattern for 0000~2359
    const regexColon = /^[0-1]\d[0-5]\d$|^2[0-3][0-5]\d$/;

    try {
      if (regexColon.test(value)) {
        calendar.updateModel(this.parseTimeFromString(value));
        calendar.updateUI();
      }
    }
    catch (err) {
      calendar.updateModel(null);
    }
    calendar.filled = value != null && value.length != 0;
  }

  searchBtnClick(): void {
    this.isSearchFormOpen = false;
    this.isInEditMode = false;
    this.isLoading = true;
    this.pageInfo.pageIndex = 0;
    this.sendSearchRequest();
  }

  sendSearchRequest(): void {
    this.restoreData();
    const getRequest: CarSequenceMaintenanceGetRequest = this.createGetRequestBean();

    this.carSequanceMaintenanceService.getCarSequences(getRequest)
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          this.table.cancelRowEdit(this.selectedRow);
          if (!isNullOrEmpty(response)) {

            //Update table
            this.tableData = this.deepCopy(response.content, this.convertTimeStampInJsonToDate)!;
            this.backupTableData = this.deepCopy(this.tableData, this.convertStringInJsonToDate)!;
            this.selectedRow = this.tableData[0];
            this.previousSelectedRow = this.selectedRow;
            this.updateTableHighlight(this.selectedRow);

            if (this.isInEditMode) {
              this.table.initRowEdit(this.selectedRow);
            }

            // Change page Info
            this.pageInfo.totalRecords = response.totalElements;
            this.pageInfo.totalPages = this.pageInfo.totalRecords / this.pageInfo.pageSize;
          }
          else {
            throw throwError(response);
          }
        },
        error: response => {
          this.tableData = [];
          this.backupTableData = [];
          this.selectedRow = null;
          this.previousSelectedRow = this.selectedRow;
          this.pageInfo.totalRecords = 0;
          this.pageInfo.totalPages = 0;

          // Handle Error Msg
          // Find error list of assignment district
          let errorCode = response.error.errorCode;
          let category = response.error.category;
          this.commonDataService.getErrorCodes(this.laasSetting.language, category).subscribe({
            next: (errorList) => {
              // find error that has the errorCode in  errorList
              let matchedError = errorList.find((error: any) => error.errorCode == errorCode)
              this.toastService.error(matchedError.errorMsg)
            }
          })
        }
      });
  }

  addNewRow(): void {
    // Prohibit user to add a new row before user fill the required data well
    if (!this.isUserInputValid(this.previousSelectedRow))
      return;

    // Add a new row to table's first row
    this.tableData.splice(0, 0, {
      seqNo: null, facility: this.laasSetting.facility, deliveryCategory: null,
      startTime: null, endTime: null,
      departureDay: null, departureTime: null, etd: null,
      assignmentDistrictId: null, pickupLocationType: null, weekDay: null,
      isWorkDay: null, dataKey: `--${this.newDataKeyId++}`
    });

    this.clearTableEdit();
    this.table.initRowEdit(this.selectedRow);
  }

  deleteSelectedRow(): void {
    this.confirmationService.confirm({
      message: this.translateService.instant('CarSequenceMaintenance.Confirm.Delete'),
      header: this.translateService.instant('CarSequenceMaintenance.Confirm.Header'),
      icon: 'pi pi-info-circle',
      accept: () => {
        this.table.cancelRowEdit(this.selectedRow);

        if (!isNullOrEmpty(this.selectedRow)) {
          const selectedRowIndex = this.tableData.indexOf(this.selectedRow!);
          if (selectedRowIndex > -1) {
            if (!isNullOrEmpty(this.tableData[selectedRowIndex].seqNo)) {
              this.addToDeletedList(this.tableData[selectedRowIndex].seqNo!);
            }

            // Remove deleted row from table
            this.tableData.splice(selectedRowIndex, 1);

            // Change selected row
            if (selectedRowIndex != this.tableData.length) {
              // Change user current selected row to the next row of the deleted row
              this.selectedRow = this.tableData[selectedRowIndex];
            }
            else { // Deleted row is the last row of the current page
              // Change user current selected row to the previous row of the deleted row
              this.selectedRow = this.tableData[selectedRowIndex - 1];
            }
            this.previousSelectedRow = this.selectedRow;
            this.table.initRowEdit(this.selectedRow);

            this.updateTableHighlight(this.selectedRow);
            this.toastService.success(this.translateService.instant('CarSequenceMaintenance.Toast.DeleteSuccess'));
          }
        }
      }
    })
  }

  saveChanges(): void {
    this.confirmationService.confirm({
      message: this.translateService.instant('CarSequenceMaintenance.Confirm.Save'),
      header: this.translateService.instant('CarSequenceMaintenance.Confirm.Header'),
      icon: 'pi pi-info-circle',
      accept: () => {
        // Prohibit user to save row if user doesn't fill the required data on the newly added row
        if (!this.isUserInputValid(this.selectedRow))
          return;

        this.isLoading = true;

        // Check user's operations on the table
        this.checkNewAndUpdatedRow();
        const savedData: CarSequenceMaintenanceSaveRequest = {
          newCarSequenceMaintenanceList: this.newCarSequenceMaintenanceList,
          updatedCarSequenceMaintenanceList: this.updatedCarSequenceMaintenanceList,
          deletedCarSequenceMaintenanceSeqNoList: this.deletedCarSequenceMaintenanceSeqNoList
        }

        this.carSequanceMaintenanceService.saveCarSequencesChanges(savedData).subscribe({
          next: () => {
            this.table.cancelRowEdit(this.selectedRow);
            this.isInEditMode = false;

            this.sendSearchRequest();
            this.toastService.success(this.translateService.instant("CarSequenceMaintenance.Toast.SaveSuccess"));
          },
          error: response => {
            this.toastService.error(this.translateService.instant('CarSequenceMaintenance.Toast.SaveFail') + response.error.errorCode)
          }
        });
      }
    })
  }

  restoreTableData(): void {
    this.confirmationService.confirm({
      message: this.translateService.instant('CarSequenceMaintenance.Confirm.Restore'),
      header: this.translateService.instant('CarSequenceMaintenance.Confirm.Header'),
      icon: 'pi pi-info-circle',
      accept: () => {
        this.restoreData();

        // Update table
        this.tableData = this.deepCopy(this.backupTableData, (key, value) => this.convertStringInJsonToDate(key, value))!;
        this.clearTableEdit();
        this.table.initRowEdit(this.selectedRow);

        this.toastService.success(this.translateService.instant('CarSequenceMaintenance.Toast.RestoreSuccess'))
      }
    })
  }

  switchEditMode(): void {
    if (this.isInEditMode) {
      this.confirmationService.confirm({
        message: this.translateService.instant('CarSequenceMaintenance.Confirm.Exit'),
        header: this.translateService.instant('CarSequenceMaintenance.Confirm.Header'),
        icon: 'pi pi-info-circle',
        accept: () => {
          this.isInEditMode = false;
          this.restoreData();

          // Update table to last saved data
          this.tableData = this.deepCopy(this.backupTableData, (key, value) => this.convertStringInJsonToDate(key, value))!;
          this.clearTableEdit();

          this.toastService.success(this.translateService.instant('CarSequenceMaintenance.Toast.ExitSuccess'))
        }
      })
    }
    else {
      this.isInEditMode = true;
      if (this.selectedRow) {
        this.table.initRowEdit(this.selectedRow);
      }
    }
  }

  onRowSelect(rowData: CarSequenceMaintenanceTableData): void {
    if (this.isInEditMode) {
      // Keep the selectedRow variable is pointing at the user's selected row
      // Whether is select or deselect
      this.selectedRow = rowData;

      // Reselect after user click on the same row
      if (this.previousSelectedRow != null && this.selectedRow.dataKey == this.previousSelectedRow.dataKey) {
        this.updateTableHighlight(this.previousSelectedRow);
        return;
      }

      if (this.isUserInputValid(this.previousSelectedRow)) {
        // Change the current selected row to the user's selected row
        this.table.cancelRowEdit(this.previousSelectedRow)
        this.table.initRowEdit(this.selectedRow);
        this.previousSelectedRow = this.selectedRow;
      }
      else {
        // Rollback the selected row to the row which doesn't fill the required data well
        this.selectedRow = this.previousSelectedRow;
        this.updateTableHighlight(this.selectedRow);
      }
    }
    else {
      this.previousSelectedRow = this.selectedRow;
    }
  }

  onTablePageChanging(event: { first: number, rows: number }) {
    this.pageInfo.pageSize = event.rows;
    this.pageInfo.pageIndex = event.first / event.rows;
    this.isLoading = true;
    this.sendSearchRequest();
  }

  onTableSorting(event: { field: string, order: number }) {
    if (this.tableData.length != 0) {
      this.sortOrder.property = event.field;
      this.sortOrder.direction = event.order;
      this.isLoading = true;
      this.sendSearchRequest();
    }
  }

  // For time format 0000 use only
  private parseTimeFromString(time: string): Date {
    const date = new Date();
    date.setHours(parseInt(time.substring(0, 2)));
    date.setMinutes(parseInt(time.substring(2, 4)));
    return date;
  }

  // Check if all the required data does the user filled
  // And ensure that's in correct format
  private isUserInputValid(rowData: CarSequenceMaintenanceTableData | null): boolean {
    if (rowData) {
      if (!rowData.deliveryCategory ||
        !rowData.startTime ||
        !rowData.endTime ||
        !rowData.departureTime) {
        this.toastService.warn(this.translateService.instant("CarSequenceMaintenance.Toast.EmptyDataError"));
        return false;
      }
    }
    return true;
  }

  // Cancel current table's row editing
  private clearTableEdit() {
    this.table.cancelRowEdit(this.selectedRow);
    this.selectedRow = this.tableData[0];
    this.previousSelectedRow = this.selectedRow;

    //Avoid the highlighted part leave on the previous row
    this.updateTableHighlight(this.selectedRow);
  }

  private restoreData(): void {
    // Clear user changed action
    this.newDataKeyId = 0;
    this.newCarSequenceMaintenanceList = [];
    this.updatedCarSequenceMaintenanceList = [];
    this.deletedCarSequenceMaintenanceSeqNoList = [];

    // Reset table current page to 1
    this.table._first = 0;
    this.table.resetScrollTop();
  }

  private createGetRequestBean(): CarSequenceMaintenanceGetRequest {
    return {
      facility: this.laasSetting.facility,
      deliveryCategory: this.searchFormGroup.value.deliveryCategory,
      startTime: this.searchFormGroup.value.startTime,
      endTime: this.searchFormGroup.value.endTime,
      departureDay: this.searchFormGroup.value.departureDay,
      departureTime: this.searchFormGroup.value.departureTime,
      etd: this.searchFormGroup.value.etd,
      assignmentDistrictId: this.searchFormGroup.value.assignmentDistrictId,
      pickupLocationType: this.searchFormGroup.value.pickupLocationType,
      weekDay: this.searchFormGroup.value.weekDay,
      isWorkDay: this.searchFormGroup.value.isWorkDay,
      pageIndex: this.pageInfo.pageIndex,
      pageSize: this.pageInfo.pageSize,
      sortOrder: this.sortOrder
    }
  }

  private checkNewAndUpdatedRow(): void {
    let saveDataIndex = 0;

    if (saveDataIndex < this.tableData.length) {
      // Check if any added row by dataKey property
      // Added row's dataKey format : --id
      for (const tableData of this.tableData) {
        if (tableData.dataKey[0] != '-') {
          break;
        }
        this.addToNewCarSequenceList(tableData);
        saveDataIndex++;
      }

      // Reverse for user add new row order
      this.newCarSequenceMaintenanceList.reverse();

      // Compare backup table data to current table data
      for (const backupData of this.backupTableData) {
        if (this.deletedCarSequenceMaintenanceSeqNoList.indexOf(backupData.seqNo!) == -1) {
          if (JSON.stringify(this.tableData[saveDataIndex]) != JSON.stringify(backupData)) {
            this.addToUpdateList(this.tableData[saveDataIndex]);
          }
          saveDataIndex++;
        }
      }
    }
  }

  // Convert date string type in JSON which come from tableData/backupTableData to Date type
  // Because deepCopy will convert Date type to String type
  private convertStringInJsonToDate(key: string, value: string): string | Date {
    if (typeof value === "string" && (key == 'startTime' || key == 'endTime' || key == 'departureTime')) {
      return new Date(value);
    }
    return value;
  }

  // Convert timestamp (number type) in Json which retrieved from back-end API to Date type
  // Because the date response from back-end API is timestamp
  private convertTimeStampInJsonToDate(key: string, value: string): string | Date {
    if (typeof value === "number" && (key == 'startTime' || key == 'endTime' || key == 'departureTime')) {
      return new Date(value);
    }
    return value;
  }

  /**
   * @template T
   * @param {T} data which need to deep copy
   * @param {(key: string, value: any) => any} reviver
   * key: the property in Json
   * value: the value paired to the key
   * @return {*}  {(T | null)}
   */
  private deepCopy<T>(data: T, reviver: (key: string, value: any) => any): T | null {
    //Check any value inside or not
    if (!data)
      return null;
    return JSON.parse(JSON.stringify(data), reviver);
  }

  // Control the selection highlighting back to the row
  private updateTableHighlight(currentRow: CarSequenceMaintenanceTableData | null): void {
    if (!currentRow)
      return;
    this.table.selection = currentRow;
    this.table.selectionKeys = {};
    this.table.selectionKeys[currentRow.dataKey] = 1;
    this.table.tableService.onSelectionChange();
  }

  private addToNewCarSequenceList(rowData: CarSequenceMaintenanceTableData): void {
    this.newCarSequenceMaintenanceList.push(rowData);
  }

  private addToUpdateList(rowData: CarSequenceMaintenanceTableData): void {
    this.updatedCarSequenceMaintenanceList.push(rowData);
  }

  private addToDeletedList(seqNo: number): void {
    this.deletedCarSequenceMaintenanceSeqNoList.push(seqNo)
  }
}
